import { useState, Fragment } from "react";
import { Link } from "wouter";
import { useGetNews } from "@workspace/api-client-react";
import { GoogleFeedAd } from "@/components/GoogleAd";
import { QueryState } from "@/components/QueryState";
import { SidebarFilter } from "@/components/SidebarFilter";
import { useTown } from "@/hooks/use-town";
import FeedAdSlot from "@/components/FeedAdSlot";
import { useCategories } from "@/hooks/use-categories";
import { useSEO } from "@/hooks/use-seo";
import { timeAgo } from "@/lib/time-ago";

const CATEGORY_COLOURS: Record<string, string> = {
  community: "bg-blue-100 text-blue-700",
  sport: "bg-green-100 text-green-700",
  business: "bg-purple-100 text-purple-700",
  council: "bg-amber-100 text-amber-700",
  events: "bg-rose-100 text-rose-700",
};

function NewsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {[1,2,3,4,5,6].map(i => (
        <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
          <div className="h-44 bg-slate-200" />
          <div className="p-4 space-y-3">
            <div className="h-3 bg-slate-200 rounded w-1/3" />
            <div className="h-4 bg-slate-200 rounded" />
            <div className="h-4 bg-slate-200 rounded w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function News() {
  useSEO({ title: "Local News", description: "Stay up to date with the latest local news from across Ayrshire — Ayr, Kilmarnock, Irvine, Prestwick, Troon and beyond." });
  const { townParam } = useTown();
  const [category, setCategory] = useState("All");
  const categories = ["All", ...useCategories("news")];
  const [search, setSearch] = useState("");

  const isBreaking = category === "Breaking";
  const { data, isLoading, isError, refetch } = useGetNews({
    town: townParam,
    category: isBreaking || category === "All" ? undefined : category,
    breaking: isBreaking ? true : undefined,
    limit: 200,
  });

  const articles = (data?.items ?? []).filter(item =>
    !search ||
    item.title.toLowerCase().includes(search.toLowerCase()) ||
    (item.summary ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">

      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 py-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Stay informed</p>
              <h1 className="text-2xl font-bold text-primary">Community News</h1>
            </div>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search news…"
              className="border border-slate-200 rounded-full px-4 py-2 text-sm w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      </div>

      <SidebarFilter items={categories} selected={category} onSelect={setCategory}>
        <div className="px-4 py-5">
        <QueryState
          isLoading={isLoading}
          isError={isError}
          isEmpty={articles.length === 0}
          onRetry={refetch}
          skeleton={<NewsSkeleton />}
          errorTitle="Couldn't load the news"
          errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
          emptyIcon="📰"
          emptyTitle="No news articles found."
          emptyBody={search ? "No stories match your search. Try different keywords." : "Try a different town or category."}
          emptyAction={search ? { label: "Clear search", onClick: () => setSearch("") } : undefined}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((article, i) => {
              const catColour = CATEGORY_COLOURS[article.category?.toLowerCase() ?? ""] ?? "bg-slate-100 text-slate-600";
              return (
                <Fragment key={article.id}>
                <Link
                  href={`/news/${article.slug ?? article.id}`}
                  className="group block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  {article.imageUrl ? (
                    <div className="relative h-44 overflow-hidden">
                      <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      {article.breaking && (
                        <div className="absolute top-3 left-3">
                          <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-white animate-pulse inline-block" />
                            Breaking
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-44 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                      <span className="text-4xl">📰</span>
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {article.category && (
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${catColour}`}>
                          {article.category}
                        </span>
                      )}
                      {article.featured && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-secondary/20 text-amber-700">Featured</span>
                      )}
                    </div>
                    <h3 className="font-bold text-slate-800 line-clamp-2 mb-1 group-hover:text-primary transition-colors">{article.title}</h3>
                    <p className="text-slate-500 text-sm line-clamp-2 mb-3">{article.summary ?? article.content}</p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>📍 {article.town}</span>
                      <span>{timeAgo(article.createdAt)}</span>
                    </div>
                  </div>
                </Link>
                {i === 2 && <div className="col-span-full"><GoogleFeedAd /></div>}
                {(i + 1) % 6 === 0 && i < articles.length - 1 && <div className="col-span-full"><FeedAdSlot placement="news" index={Math.floor(i / 6)} /></div>}
                </Fragment>
              );
            })}
          </div>
        </QueryState>
        </div>
      </SidebarFilter>
    </div>
  );
}
